/* eslint-disable react/jsx-props-no-spreading */
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import SendIcon from "@mui/icons-material/Send"
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied"
import { Box, DialogContentText, Stack, useTheme } from "@mui/material"
import { ReactElement, useEffect, useState } from "react"
import { BallTriangle } from "react-loading-icons"
import { useNavigate } from "react-router-dom"

import Feedback, { RATE_FIELD } from "./feedback"
import { NETWORK_TO_DNO_FRIENDLY_ABBREVIATION_DISPATCHER } from "../../../../constants/dnos"
import { InstallerApplicationStatuses } from "../../../../constants/enums"
import { CURRENT_QUESTION } from "../../../../constants/generalConstants"
import { INSTALLER_SINGLE_APPLICATION_BASE } from "../../../../router/routes"
import useInstallerFeedback from "../../../../services/middleware/hooks/installerFeedback/useInstallerFeedback"
import useInstallerSingleApplication from "../../../../services/middleware/hooks/installerInterface/useInstallerSingleApplication"
import { GRID_SPACING } from "../../../../themes/defaultThemeParams"
import { getValue } from "../../../../utils/general/generalUtils"
import {
    mapAmendFailureText,
    scrollToTop,
} from "../../../../utils/submitApplication/submitApplicationUtils"
import { ApprovalsIcon } from "../../../icons/icons"
import AppProgressBar from "../../../loading/progressBar/appProgressBar"
import SuccessConfirmationTemplate from "../../../popUps/success/successConfirmationTemplate"
import { AppCard } from "../../../surfaces/appCard"
import DisplayIf from "../../../wrappers/displayIf"

const ASSESSING = "ASSESSING"
const ESCALATED = "ESCALATED"
const APPROVED = "APPROVED"
const RE_ESCALATED = "RE_ESCALATED"
const RE_APPROVED = "RE_APPROVED"

const TIMEOUT = 120 * 1000 // 120 seconds

interface ISubmitApplicationSuccessProps {
    isOpen: boolean
    applicationId: string
    dno: string
    isAmendmentMode: boolean
}

/**
 * Success dialogue shown when an application is successfully submitted
 */
const SubmitApplicationSuccess = ({
    isOpen,
    applicationId,
    dno,
    isAmendmentMode,
}: ISubmitApplicationSuccessProps): ReactElement => {
    const navigate = useNavigate()
    const theme = useTheme()
    const { postResponse } = useInstallerFeedback()
    const [timerExpired, setTimerExpired] = useState(false)
    const [feedback, setFeedback] = useState({
        rating: -1,
        comment: "",
    })

    const dnoDisplayName =
        getValue(NETWORK_TO_DNO_FRIENDLY_ABBREVIATION_DISPATCHER, dno) ?? dno

    useEffect(() => {
        const timer = setTimeout(() => {
            setTimerExpired(true)
        }, TIMEOUT)

        // Cleanup function to clear the timer when the component unmounts
        return () => clearTimeout(timer)
    }, [])

    const { application } = useInstallerSingleApplication(
        applicationId,
        !timerExpired
    )

    const getPercentage = (): [number, string] => {
        if (application?.lastAmendmentFailed) {
            return [100, ASSESSING]
        }
        if (!application) {
            return [0, ASSESSING]
        }
        if (
            application.applicationStatus ===
            InstallerApplicationStatuses.awaitingAssessment
        ) {
            return [25, ASSESSING]
        }
        if (
            application.applicationStatus ===
            InstallerApplicationStatuses.imageScanning
        ) {
            return [45, ASSESSING]
        }
        if (
            application.applicationStatus ===
            InstallerApplicationStatuses.underAssessment
        ) {
            return [80, ASSESSING]
        }
        if (
            application.applicationStatus ===
            InstallerApplicationStatuses.escalatedToDno
        ) {
            return [100, ESCALATED]
        }
        if (
            application.applicationStatus ===
            InstallerApplicationStatuses.autoApproved
        ) {
            return [100, APPROVED]
        }
        if (
            application.applicationStatus ===
            InstallerApplicationStatuses.reAutoApproved
        ) {
            return [100, RE_APPROVED]
        }
        if (
            application.applicationStatus ===
            InstallerApplicationStatuses.reEscalatedToDno
        ) {
            return [100, RE_ESCALATED]
        }
        return [85, ASSESSING]
    }

    const escalateIcon = (props: any): ReactElement => (
        <SendIcon color="primary" {...props} />
    )
    const approve = (props: any): ReactElement => (
        <ApprovalsIcon color="success" {...props} />
    )

    const timeout = (props: any): ReactElement => (
        <AccessTimeIcon color="primary" {...props} />
    )

    const sadIcon = (props: any): ReactElement => (
        <SentimentVeryDissatisfiedIcon color="primary" {...props} />
    )

    const loadIcon = (): ReactElement => (
        <BallTriangle
            stroke={theme.palette.primary.main}
            height="200px"
            width="200px"
            speed={1.2}
            strokeOpacity={1}
            strokeWidth={1.2}
        />
    )

    /** Submit installer feedback (if added) Navigate to device summary page and scroll to top */
    const onClickNext = (): void => {
        navigate(`${INSTALLER_SINGLE_APPLICATION_BASE}/${applicationId}`)
        // Send feedback if a rating has been selected (1-5)
        if (feedback.rating >= 1 && feedback.rating <= 5) {
            postResponse(CURRENT_QUESTION, feedback.rating, feedback.comment)
        }
        scrollToTop(document)
    }

    const updateRating = (
        field: RATE_FIELD,
        value: number | string | null
    ): void => {
        if (value === null) return
        switch (field) {
            case RATE_FIELD.RATING:
                setFeedback({ ...feedback, rating: value as number })
                break
            case RATE_FIELD.COMMENT:
                setFeedback({ ...feedback, comment: value as string })
                break
            default:
                break
        }
    }

    const whatHappensNext = (
        title: string,
        subtitle: string,
        whatHappensNextText: string,
        icon: (props: any) => ReactElement = loadIcon
    ): ReactElement => (
        <SuccessConfirmationTemplate
            title={title}
            subtitle={subtitle}
            nextButtonTitle="Click to view the application"
            onClickNext={onClickNext}
            isOpen={isOpen}
            successId={applicationId}
            successIdName="ID"
            color="primary"
            icon={icon}
        >
            <Stack spacing={GRID_SPACING}>
                <DisplayIf condition={!timerExpired}>
                    <Box sx={{ width: "100%" }} paddingY={2}>
                        <AppProgressBar
                            variant="determinate"
                            value={getPercentage()[0]}
                        />
                    </Box>
                </DisplayIf>
                <AppCard
                    sx={{
                        backgroundColor: "background.contentBackground",
                    }}
                >
                    <DialogContentText
                        fontWeight={800}
                        fontSize="body1.fontSize"
                    >
                        What happens next?
                    </DialogContentText>
                    <DialogContentText fontSize="body1.fontSize">
                        {whatHappensNextText}
                    </DialogContentText>
                </AppCard>
            </Stack>
        </SuccessConfirmationTemplate>
    )

    if (application?.lastAmendmentFailed) {
        const mapAmendedFailureText = mapAmendFailureText(
            application?.amendmentFailureCategory || "",
            dnoDisplayName
        )
        return whatHappensNext(
            mapAmendedFailureText.title,
            mapAmendedFailureText.subtitle,
            mapAmendedFailureText.whatHappensNext,
            sadIcon
        )
    }

    if (getPercentage()[1] === ESCALATED) {
        return (
            <SuccessConfirmationTemplate
                title={`Your application has been escalated to ${dnoDisplayName}`}
                subtitle={`We checked your application but were unfortunately unable to approve it. We've passed it to ${dnoDisplayName} who will review it and get back to you with the outcome.`}
                nextButtonTitle="Click to view the application"
                onClickNext={onClickNext}
                isOpen={isOpen}
                successId={applicationId}
                successIdName="ID: "
                color="primary"
                icon={escalateIcon}
            >
                <Feedback
                    title="We'd love your feedback"
                    question={CURRENT_QUESTION}
                    onUpdate={updateRating}
                />
            </SuccessConfirmationTemplate>
        )
    }

    if (getPercentage()[1] === RE_ESCALATED) {
        return whatHappensNext(
            `Your amended application has been escalated to ${dnoDisplayName}`,
            `We checked your amendment but were unfortunately unable to approve it. We've passed it to ${dnoDisplayName} who will review it and get back to you with the outcome.`,
            `${dnoDisplayName} will review it and get back to you with the outcome. You must wait for their response before you can proceed with commissioning. Connect Direct will not notify the customer during this re-assessment process`,
            escalateIcon
        )
    }

    if (getPercentage()[1] === RE_APPROVED) {
        return whatHappensNext(
            "Your amended application has been approved!",
            "Your amended application passed our tests and has been (re) auto-approved!",
            "This means your amendment has been accepted. You can now proceed with the commissioning process, which can be kick-started from the application summary page. No customer communications were issued by Connect Direct during this process",
            approve
        )
    }

    if (getPercentage()[1] === APPROVED) {
        return (
            <SuccessConfirmationTemplate
                title="Your application has been approved!"
                subtitle="Your application passed our tests and has been auto-approved. You can proceed with the installation!"
                nextButtonTitle="Click to view the application"
                onClickNext={onClickNext}
                isOpen={isOpen}
                successId={applicationId}
                successIdName="ID: "
                color="success"
                icon={approve}
            >
                <Feedback
                    title="We'd love your feedback"
                    question={CURRENT_QUESTION}
                    onUpdate={updateRating}
                />
            </SuccessConfirmationTemplate>
        )
    }

    if (timerExpired) {
        return whatHappensNext(
            "Assessment taking a little longer than expected",
            "We are sorry, but the assessment of your application is taking a little longer than expected. You can leave this page and visit the below link for updates on the application whenever you like!",
            "The assessment of this application is taking a little longer than expected. You can leave this page and visit the below link for updates on the application whenever you like! Your application has been successfully submitted so no data will be lost!",
            timeout
        )
    }

    if (isAmendmentMode) {
        return whatHappensNext(
            "Reassessing your application",
            "We are are re-assessing your updated application right now! This typically takes 1-2 minutes",
            `If it passes our checks, it will get (re) auto-approved immediately. If not, we will pass it back to ${dnoDisplayName} for review! If it gets (re) escalated, ${dnoDisplayName} will have to approve it again before you can proceed with commissioning. If it gets (re) auto-approved, you can proceed with commissioning immediately! You can leave this page and visit the below link for updates on the application whenever you like!`
        )
    }

    return whatHappensNext(
        "Application Under Assessment",
        "We are assessing your application right now! This typically takes 1-2 minutes",
        `If it passes our checks, it will get auto-approved immediately. If not, we will pass it to ${dnoDisplayName} for review! This usually takes around a minute. You can leave this page and visit the below link for updates on the application whenever you like!`
    )
}
export default SubmitApplicationSuccess
